"use client"

import Link from "next/link"
import { ArrowUpRight } from "lucide-react"

import { ToolIllustration } from "@/components/tools/tool-illustration"
import { cn } from "@/lib/utils"
import {
  buildFertilizerToolUrl,
  type HerbicideApplicationType,
  buildSoilTempToolUrl,
  buildHerbicideTimingToolUrl,
  buildWateringScheduleUrl,
} from "@/lib/result-tool-urls"
import { contentAnalytics } from "@/lib/analytics"

interface ResultToolDeeplinksProps {
  grassType: string
  zip: string
  herbicideApplicationType?: HerbicideApplicationType
  herbicideTargetWeeds?: string[]
  className?: string
  /** The chemical-safety validator failed closed on this result, so the screen
   *  states we are withholding products and immediate steps. Drops the
   *  herbicide-timing tile, whose whole job is telling the reader when to apply
   *  a pre- or post-emergent. The other three are cultural calculators
   *  (fertilizer, soil temperature, watering), which is exactly the guidance a
   *  'cultural_only' fail-closed result keeps. */
  suppressHerbicideTiming?: boolean
  /** Result-funnel notification. Fires alongside (never instead of) the
   *  internal-link content event so the host can report the tap as the
   *  `open_tool` action its `result_state_shown` advertised. */
  onNextAction?: (toolName: string) => void
}

interface ToolTile {
  name: string
  blurb: string
  href: string
  /** Base tool route (no query) — keys the shared ToolIllustration artwork. */
  toolPath: string
}

export function ResultToolDeeplinks({
  grassType,
  zip,
  herbicideApplicationType,
  herbicideTargetWeeds,
  className,
  suppressHerbicideTiming = false,
  onNextAction,
}: ResultToolDeeplinksProps) {
  const tiles: ToolTile[] = [
    {
      name: 'Fertilizer calculator',
      blurb: 'NPK + amount tuned to your grass and season',
      href: buildFertilizerToolUrl({ grassType }),
      toolPath: '/tools/fertilizer-calculator',
    },
    {
      name: 'Soil temperature',
      blurb: 'Live 5-year soil-temp tracker for your ZIP',
      href: buildSoilTempToolUrl({ zip }),
      toolPath: '/tools/soil-temperature',
    },
    ...(suppressHerbicideTiming ? [] : [{
      name: 'Herbicide timing',
      blurb: herbicideTargetWeeds?.length
        ? `Timing for ${herbicideTargetWeeds.slice(0, 2).join(', ')}`
        : 'When to apply pre- and post-emergents',
      href: buildHerbicideTimingToolUrl({
        grassType,
        zip,
        applicationType: herbicideApplicationType,
        targetWeeds: herbicideTargetWeeds,
      }),
      toolPath: '/tools/herbicide-timing',
    } as ToolTile]),
    {
      name: 'Watering schedule',
      blurb: 'Weekly inches and irrigation cadence',
      href: buildWateringScheduleUrl(),
      toolPath: '/tools/watering-schedule',
    },
  ]

  const handleClick = (tile: ToolTile) => {
    contentAnalytics.internalLinkClicked('home-result', tile.href, tile.name)
    onNextAction?.(tile.name)
  }

  return (
    <section
      className={cn("w-full", className)}
      aria-labelledby="result-tool-deeplinks-heading"
    >
      <div className="mb-4">
        <h2
          id="result-tool-deeplinks-heading"
          className="text-base md:text-lg font-bold text-gray-900 leading-tight"
        >
          Tools tuned to your lawn
        </h2>
        <p className="text-[12.5px] text-gray-500 mt-0.5">
          Free calculators, opened ready to run with your grass and ZIP.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((tile) => (
          <Link
            key={tile.name}
            href={tile.href}
            onClick={() => handleClick(tile)}
            className="group flex flex-col rounded-xl border border-gray-200 bg-white p-3 md:p-4 hover:border-green-300 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-all duration-200 min-w-0"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-green-50">
                <ToolIllustration href={tile.toolPath} />
              </div>
              <ArrowUpRight
                className="w-4 h-4 text-gray-300 group-hover:text-green-700 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-200"
                strokeWidth={2}
              />
            </div>
            <div className="font-semibold text-[13.5px] text-gray-900 leading-snug mb-1">
              {tile.name}
            </div>
            <p className="text-[11.5px] text-gray-500 leading-snug line-clamp-2 flex-1">
              {tile.blurb}
            </p>
          </Link>
        ))}
      </div>
    </section>
  )
}
